/**
 * Audio Capture System
 *
 * Handles microphone permission, audio capture, chunking, and volume detection.
 * Integrates with the global store for state management.
 */

import { voiceAgentStore } from "../../store/voiceAgentStore";

export interface AudioCaptureConfig {
  chunkSizeBytes: number; // Target chunk size in bytes (e.g., 4096, 8192)
  sampleRate: number; // Audio sample rate (e.g., 16000, 48000)
  channelCount: number; // Mono (1) or Stereo (2)
  vadThreshold: number; // Voice Activity Detection threshold (0-1)
  logChunks: boolean; // Whether to log chunks to console
  onAudioChunk?: (binaryData: Uint8Array, bytes: Uint8Array) => void; // Callback for audio chunks (binary)
  processorBufferSize?: number; // Size (in frames) for ScriptProcessor; smaller => lower latency (powers of two)
}

const DEFAULT_CONFIG: AudioCaptureConfig = {
  chunkSizeBytes: 4096, // ~0.25 seconds at 16kHz mono 16-bit
  sampleRate: 16000, // Common for voice AI
  channelCount: 1, // Mono
  vadThreshold: 0.01, // Threshold for detecting speech
  logChunks: true,
  processorBufferSize: 512,
};

export class AudioCaptureSystem {
  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private analyserNode: AnalyserNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private config: AudioCaptureConfig;
  private isCapturing = false;
  private audioBuffer: Int16Array[] = [];
  private targetBufferSize: number;
  private animationFrameId: number | null = null;

  constructor(config: Partial<AudioCaptureConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    // Calculate how many samples we need for the target chunk size
    // Each sample is 2 bytes (Int16), so divide by 2
    this.targetBufferSize = Math.floor(this.config.chunkSizeBytes / 2);
  }

  /**
   * Request microphone permission and start capturing audio
   * Returns true if successful, false if permission denied
   */
  async startCapture(): Promise<boolean> {
    try {
      console.log("🎤 Requesting microphone permission...");

      // Request microphone access
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: this.config.sampleRate,
          channelCount: this.config.channelCount,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      console.log("✅ Microphone permission granted");

      // Create audio context
      this.audioContext = new AudioContext({
        sampleRate: this.config.sampleRate,
      });

      // Create analyser for volume detection
      this.analyserNode = this.audioContext.createAnalyser();
      this.analyserNode.fftSize = 2048;
      this.analyserNode.smoothingTimeConstant = 0.8;

      // Create source from media stream
      this.sourceNode = this.audioContext.createMediaStreamSource(
        this.mediaStream
      );

      // Create ScriptProcessor for audio capture
      // Note: ScriptProcessor is deprecated but more compatible than AudioWorklet
      // Use the configured processor buffer size (power-of-two recommended)
      const bufferSize = this.config.processorBufferSize || 512;
      const processor = this.audioContext.createScriptProcessor(
        bufferSize,
        this.config.channelCount,
        this.config.channelCount
      );

      processor.onaudioprocess = (event) => {
        if (!this.isCapturing) return;

        const store = voiceAgentStore;

        // If muted, we don't want to collect NEW samples, but we might need to flush pending ones
        if (store.isMuted) {
          // If we have data in the buffer, flush it (graceful stop)
          if (this.audioBuffer.length > 0) {
            this.flushAudioBuffer();
          }
          return;
        }

        const inputData = event.inputBuffer.getChannelData(0);

        // Convert Float32Array to Int16Array
        const int16Data = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          int16Data[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }

        // Add to buffer
        this.audioBuffer.push(int16Data);

        // Check if we have enough data for a chunk
        const totalSamples = this.audioBuffer.reduce(
          (sum, arr) => sum + arr.length,
          0
        );

        if (totalSamples >= this.targetBufferSize) {
          this.flushAudioBuffer();
        }
      };

      // Connect nodes: source -> analyser -> processor -> destination
      this.sourceNode.connect(this.analyserNode);
      this.analyserNode.connect(processor);
      processor.connect(this.audioContext.destination);

      this.isCapturing = true;

      // Start volume monitoring
      this.startVolumeMonitoring();

      console.log("🎤 Audio capture started");
      console.log(
        `📊 Config: ${this.config.sampleRate}Hz, ${this.config.channelCount} channel(s), ${this.config.chunkSizeBytes} bytes/chunk`
      );

      return true;
    } catch (error) {
      console.error("❌ Microphone permission denied or error:", error);

      if (error instanceof DOMException) {
        if (
          error.name === "NotAllowedError" ||
          error.name === "PermissionDeniedError"
        ) {
          console.error("🚫 User denied microphone permission");
        } else if (error.name === "NotFoundError") {
          console.error("🚫 No microphone found");
        } else {
          console.error("🚫 Microphone error:", error.message);
        }
      }

      return false;
    }
  }

  /**
   * Flush accumulated audio buffer to a chunk
   */
  private flushAudioBuffer() {
    // const store = useFiBIVoiceAgentStore.getState();

    // Don't send if muted - WAIT! We actually DO want to send if we are flushing the tail.
    // The check for stopping collection is now in onaudioprocess.
    // So here we just process whatever is in the buffer.

    /* 
    if (store.isMuted) {
      this.audioBuffer = [];
      return;
    }
    */

    // Calculate total size
    const totalSamples = this.audioBuffer.reduce(
      (sum, arr) => sum + arr.length,
      0
    );

    if (totalSamples === 0) return;

    // Create combined array
    const combinedArray = new Int16Array(totalSamples);
    let offset = 0;
    for (const chunk of this.audioBuffer) {
      combinedArray.set(chunk, offset);
      offset += chunk.length;
    }

    // Send out as many full chunks as we can in this flush (avoid leaving many full chunks waiting)
    const chunksToSend: Int16Array[] = [];
    let readPos = 0;
    while (totalSamples - readPos >= this.targetBufferSize) {
      const chunkArray = combinedArray.subarray(
        readPos,
        readPos + this.targetBufferSize
      );
      // Push a copy to avoid referencing underlying array slices
      chunksToSend.push(new Int16Array(chunkArray));
      readPos += this.targetBufferSize;
    }

    // Remaining samples we keep as buffer
    const remainingCount = totalSamples - readPos;
    const remainingArray =
      remainingCount > 0 ? combinedArray.slice(readPos) : new Int16Array(0);

    // Note: We handle per-chunk logging below inside the send loop.

    // Send all full chunks via callback (Uint8Array). This reduces latency by sending smaller chunks faster.
    if (this.config.onAudioChunk && chunksToSend.length > 0) {
      for (const chunk of chunksToSend) {
        const bytes = new Uint8Array(
          chunk.buffer,
          chunk.byteOffset,
          chunk.byteLength
        );
        // Ensure the bytes length exactly matches config.chunkSizeBytes to avoid slight over/under send
        // (trim any accidental extra bytes). We intentionally don't pad short chunks here.
        let outBytes = bytes;
        if (bytes.length !== this.config.chunkSizeBytes) {
          if (bytes.length > this.config.chunkSizeBytes) {
            // Truncate to the configured chunk size
            outBytes = bytes.subarray(0, this.config.chunkSizeBytes);
          } else {
            // If bytes shorter than expected, log a warning and send as-is (we avoid padding to prevent distortion)
            console.warn(
              `⚠️ Chunk shorter than configured chunkSizeBytes: ${bytes.length} bytes vs ${this.config.chunkSizeBytes} bytes`
            );
          }
        }
        this.config.onAudioChunk(outBytes, outBytes);

        if (this.config.logChunks) {
          console.log(
            `📤 Flushed small chunk: samples=${chunk.length}, bytes=${
              outBytes.length
            } (${(outBytes.length / 1000).toFixed(1)} KB)`
          );
        }
      }
    }

    // Reset buffer, keeping any remaining data
    if (remainingArray.length > 0) {
      this.audioBuffer = [remainingArray];
    } else {
      this.audioBuffer = [];
    }
  }

  /**
   * Monitor volume levels for visual feedback
   */
  private startVolumeMonitoring() {
    if (!this.analyserNode) return;

    const dataArray = new Uint8Array(this.analyserNode.frequencyBinCount);

    const updateVolume = () => {
      if (!this.isCapturing || !this.analyserNode) return;

      this.analyserNode.getByteTimeDomainData(dataArray);

      // Calculate RMS (Root Mean Square) for volume
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const normalized = (dataArray[i] - 128) / 128;
        sum += normalized * normalized;
      }
      const rms = Math.sqrt(sum / dataArray.length);

      // Update store with volume
      const store = voiceAgentStore;

      if (!store.isMuted) {
        store.userVolume = rms;

        // Voice Activity Detection
        if (rms > this.config.vadThreshold) {
          if (store.speaker !== "user") {
            store.speaker = "user";
          }
        } else {
          if (store.speaker === "user" && store.status === "connected") {
            store.speaker = "idle";
          }
        }
      } else {
        // Reset volume when muted
        store.userVolume = 0;
        if (store.speaker === "user") {
          store.speaker = "idle";
        }
      }

      this.animationFrameId = requestAnimationFrame(updateVolume);
    };

    updateVolume();
  }

  /**
   * Stop capturing audio and cleanup resources
   */
  stopCapture() {
    console.log("🛑 Stopping audio capture...");

    this.isCapturing = false;

    // Cancel animation frame
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Flush any remaining audio
    if (this.audioBuffer.length > 0) {
      this.flushAudioBuffer();
    }

    // Stop all tracks
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }

    // Disconnect and cleanup audio nodes
    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }

    if (this.analyserNode) {
      this.analyserNode.disconnect();
      this.analyserNode = null;
    }

    // Close audio context
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    // Reset volume in store
    const store = voiceAgentStore;
    store.userVolume = 0;
    store.speaker = "idle";

    console.log("✅ Audio capture stopped");
  }

  // Removed unused base64 helper to avoid lint warnings; we send binary chunks directly.

  /**
   * Update configuration
   */
  updateConfig(config: Partial<AudioCaptureConfig>) {
    // Validate small chunk sizes to avoid invalid or too-small values
    if (config.chunkSizeBytes !== undefined && config.chunkSizeBytes < 4) {
      console.warn(
        "⚠️ Received tiny chunkSizeBytes, forcing minimum of 4 bytes"
      );
      config.chunkSizeBytes = 4;
    }
    // Ensure chunk size is an even number of bytes (PCM16 requires even bytes)
    if (
      config.chunkSizeBytes !== undefined &&
      config.chunkSizeBytes % 2 !== 0
    ) {
      console.warn(
        "⚠️ Received chunkSizeBytes not multiple of 2; rounding down to nearest even value"
      );
      config.chunkSizeBytes = config.chunkSizeBytes - 1; // enforce even value
    }

    this.config = { ...this.config, ...config };
    this.targetBufferSize = Math.floor(this.config.chunkSizeBytes / 2);
    console.log("⚙️ Audio config updated:", this.config);
  }

  /**
   * Check if currently capturing
   */
  isActive(): boolean {
    return this.isCapturing;
  }

  /**
   * Get current configuration
   */
  getConfig(): AudioCaptureConfig {
    return { ...this.config };
  }
}

// Global singleton instance
let audioCaptureInstance: AudioCaptureSystem | null = null;

/**
 * Get or create the global audio capture instance
 */
export function getAudioCapture(
  config?: Partial<AudioCaptureConfig>
): AudioCaptureSystem {
  if (!audioCaptureInstance) {
    audioCaptureInstance = new AudioCaptureSystem(config);
  } else if (config) {
    audioCaptureInstance.updateConfig(config);
  }
  return audioCaptureInstance;
}

/**
 * Destroy the global audio capture instance
 */
export function destroyAudioCapture() {
  if (audioCaptureInstance) {
    audioCaptureInstance.stopCapture();
    audioCaptureInstance = null;
  }
}
